import {tx, ptx, usePageText} from '../lib/page-text';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth-store';

interface Props {
  streamId: string;
  streamKey: string | undefined;
}

interface Message {
  id?: string;
  type: 'chat' | 'system' | 'welcome' | 'error';
  authorId?: string | null;
  authorName?: string;
  body?: string;
  at?: string;
  isHost?: boolean;
  viewers?: number;
}

// Chat WebSocket — always on the origin the console is served from
// (live.kardocloud.com, kardocloud.com/app, or the Vite proxy on localhost).
// CloudFront sends /v1/* to the API, which upgrades the socket, and holds it
// open (checked 2026-09-19: 101, welcome frame, still open after 2 idle min).
//
// It used to go straight to chat.kardocloud.com, from a time CloudFront
// answered the upgrade with 502. That hostname was later pointed at the
// separate Go chat service, which speaks the room protocol and answers
// "missing room" to a streamKey — so the console's chat never connected.
// The public watch page already used kardocloud.com/v1/chat/socket.
const WS_BASE = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/v1/chat/socket`;

export function ChatPanel({ streamId, streamKey }: Props) {
  usePageText();
  const token = useAuth((s) => s.token);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [viewers, setViewers] = useState(0);
  const [connected, setConnected] = useState(false);
  const [everConnected, setEverConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // history fetch on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/v1/streams/${streamId}/chat?limit=50`);
        if (!r.ok) return;
        const data = await r.json();
        if (!alive) return;
        setMessages(data.messages.map((m: any) => ({
          type: 'chat', id: m.id, authorId: m.authorId, authorName: m.authorName, body: m.body, at: m.createdAt,
        })));
        setViewers(data.viewers ?? 0);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [streamId]);

  // ws connect — with reconnect. A dropped socket used to leave a red
  // "offline" badge on the page until a reload, which read as "chat is
  // broken" rather than "the connection blinked".
  useEffect(() => {
    if (!streamKey) return;
    let alive = true;
    let retry: number | null = null;
    let delay = 1500;
    const open = () => {
      if (!alive) return;
      setError(null);
      const url = `${WS_BASE}?streamKey=${encodeURIComponent(streamKey)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); setEverConnected(true); delay = 1500; };
      ws.onclose = () => {
        setConnected(false);
        if (alive) { retry = window.setTimeout(open, delay); delay = Math.min(delay * 2, 15000); }
      };
      ws.onerror = () => setError('Chat connection error');
      ws.onmessage = (evt) => {
        try {
          const msg: Message = JSON.parse(evt.data);
          if (msg.viewers != null) setViewers(msg.viewers);
          setMessages((prev) => [...prev.slice(-199), msg]);
        } catch { /* ignore */ }
      };
    };
    open();
    return () => { alive = false; if (retry) clearTimeout(retry); wsRef.current?.close(); wsRef.current = null; };
  }, [streamKey, token]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function send() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const body = draft.trim();
    if (!body) return;
    ws.send(JSON.stringify({ type: 'chat', body }));
    setDraft('');
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: 480, padding: 0, overflow: 'hidden' }}>
      <div className="row-between" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>{tx("Live chat")}</h3>
          <span className={`badge ${connected ? 'success' : ''}`}>
            {connected ? tx("● Open") : everConnected ? tx("Reconnecting…") : tx("Connecting…")}
          </span>
        </div>
        <div className="muted" style={{ fontSize: 11 }}>{viewers}{tx(" viewer")}{viewers === 1 ? '' : 's'}</div>
      </div>

      <div ref={scrollerRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {messages.length === 0 && (
          <div className="muted" style={{ textAlign: 'center', padding: 16, fontSize: 12 }}>{tx("No messages yet. Be the first to say hi.")}</div>
        )}
        {messages.map((m, i) => {
          if (m.type === 'system' || m.type === 'welcome') {
            return (
              <div key={i} className="muted" style={{ fontSize: 11, fontStyle: 'italic', textAlign: 'center', padding: '2px 0' }}>
                {m.body ?? (m.type === 'welcome' ? ptx("you joined as {v0}{v1}", {v0: (m.authorName), v1: (m.isHost ? ' (host)' : '')}) : '')}
              </div>
            );
          }
          if (m.type === 'error') {
            return <div key={i} style={{ fontSize: 12, color: 'var(--danger)', padding: '2px 0' }}>{m.body}</div>;
          }
          return (
            <div key={i} style={{ fontSize: 13, lineHeight: 1.4 }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{m.authorName}</span>
              <span style={{ color: 'var(--text-dim)' }}>: {m.body}</span>
            </div>
          );
        })}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', padding: 12 }}>
        {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>{error}</div>}
        <div className="flex gap">
          <input
            className="input"
            placeholder={connected ? tx("Send a message…") : tx("Connecting…")}
            value={draft}
            disabled={!connected}
            maxLength={500}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            style={{ flex: 1 }}
          />
          <button className="btn" onClick={send} disabled={!connected || !draft.trim()}>{tx("Send")}</button>
        </div>
      </div>
    </div>
  );
}
